import React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import Button from './Button';

export interface CustomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  placement?: 'left' | 'right' | 'top' | 'bottom';
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  showCloseButton?: boolean;
  onSave?: () => void;
  saveButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
  isSaving?: boolean;
  borderRadius?: string | number;
  headerBorderWidth?: string | number;
  footerBorderWidth?: string | number;
  borderRight?: string;
  borderLeft?: string;
  borderRadiusSides?: 'all' | 'left' | 'right' | 'top' | 'bottom' | 'top-bottom' | 'left-right';
  /** Ancho del drawer — sobreescribe el tamaño del size prop */
  width?: string | number;
  /** Alto del drawer */
  height?: string | number;
  /** Padding uniforme del body (shorthand) */
  contentPadding?: string | number;
  /** Padding horizontal del body — sobreescribe contentPadding en eje x */
  contentPaddingX?: string | number;
  /** Padding vertical del body — sobreescribe contentPadding en eje y */
  contentPaddingY?: string | number;
  /** Margen entre el panel del drawer y los bordes de la ventana */
  outerPadding?: string | number;
  layout?: 'form' | 'menu' | 'list' | 'info' | 'default';
}

export const CustomDrawer: React.FC<CustomDrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  placement = 'right',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  onSave,
  saveButtonText = 'Guardar',
  cancelButtonText = 'Cancelar',
  isLoading: _isLoading = false,
  isSaving = false,
  borderRadius = 'md',
  headerBorderWidth = '1px',
  footerBorderWidth = '1px',
  borderRight,
  borderLeft,
  borderRadiusSides,
  width,
  height,
  contentPadding = '24px',
  contentPaddingX,
  contentPaddingY,
  outerPadding,
  layout = 'default',
}) => {
  const toCSSValue = (val: string | number) =>
    typeof val === 'number' ? `${val}px` : val;

  const getLayoutStyles = () => {
    switch (layout) {
      case 'form':   return { spacing: '20px', align: 'stretch' as const };
      case 'menu':   return { spacing: '8px',  align: 'stretch' as const };
      case 'list':   return { spacing: '12px', align: 'stretch' as const };
      case 'info':   return { spacing: '16px', align: 'stretch' as const };
      default:       return { spacing: '16px', align: 'stretch' as const };
    }
  };
  const layoutStyles = getLayoutStyles();
  const headerBg = useColorModeValue('white', 'gray.800');
  const headerBorderColor = useColorModeValue('gray.200', 'gray.700');
  const bodyBg = useColorModeValue('white', 'gray.800');

  // Aplicar borderRadius según borderRadiusSides o placement
  const getBorderRadius = () => {
    if (!borderRadius) return undefined;
    
    // Si se proporciona borderRadiusSides, usarlo
    if (borderRadiusSides) {
      switch (borderRadiusSides) {
        case 'all':
          return borderRadius;
        case 'left':
          return `${borderRadius} 0 0 ${borderRadius}`;
        case 'right':
          return `0 ${borderRadius} ${borderRadius} 0`;
        case 'top':
          return `${borderRadius} ${borderRadius} 0 0`;
        case 'bottom':
          return `0 0 ${borderRadius} ${borderRadius}`;
        case 'top-bottom':
          return `${borderRadius} ${borderRadius} ${borderRadius} ${borderRadius}` ;
        case 'left-right':
          return `${borderRadius} 0 0 ${borderRadius}`;
        default:
          return borderRadius;
      }
    }
    
    // Si no, usar lógica de placement
    if (placement === 'right') return `0 ${borderRadius} ${borderRadius} 0`;
    if (placement === 'left') return `${borderRadius} 0 0 ${borderRadius}`;
    if (placement === 'top') return `0 0 ${borderRadius} ${borderRadius}`;
    if (placement === 'bottom') return `${borderRadius} ${borderRadius} 0 0`;
    return borderRadius;
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement={placement}
      onClose={onClose}
      size={size}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEsc={closeOnEsc}
    >
      <DrawerOverlay />
      <DrawerContent
        bg={bodyBg}
        borderRadius={getBorderRadius()}
        borderRight={borderRight}
        borderLeft={borderLeft}
        overflow="hidden"
        maxW={width ?? undefined}
        h={outerPadding
          ? `calc(100% - calc(${toCSSValue(outerPadding)} * 2))`
          : height ?? undefined}
        mt={outerPadding}
        mb={outerPadding}
        mr={placement === 'right' ? outerPadding : undefined}
        ml={placement === 'left' ? outerPadding : undefined}
      >
        {showCloseButton && <DrawerCloseButton />}

        {title && (
          <DrawerHeader
            borderBottomWidth={headerBorderWidth}
            borderBottomColor={headerBorderColor}
            bg={headerBg}
            fontWeight="700"
            fontSize="lg"
          >
            {title}
          </DrawerHeader>
        )}

        <DrawerBody
          px={contentPaddingX ?? contentPadding}
          py={contentPaddingY ?? contentPadding}
        >
          <VStack spacing={layoutStyles.spacing} align={layoutStyles.align}>
            {children}
          </VStack>
        </DrawerBody>

        {footer || (onSave && (
          <DrawerFooter borderTopWidth={footerBorderWidth} borderTopColor={headerBorderColor}>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSaving}>
              {cancelButtonText}
            </Button>
            {onSave && (
              <Button
                variant="brand"
                onClick={onSave}
                isLoading={isSaving}
                
              >
                {saveButtonText}
              </Button>
            )}
          </DrawerFooter>
        ))}
      </DrawerContent>
    </Drawer>
  );
};

export default CustomDrawer;
